import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Undo2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { TYPE_LABEL, errMsg, type Item, type PizzaTopping } from './types';
import './menu.css';

/** The daily 86 list — everything currently sold out, one tap to restore. */
export function SoldOut() {
  const { rid, sid } = useScope();
  const base = useBase();

  const [items, setItems] = useState<Item[] | null>(null);
  const [toppings, setToppings] = useState<PizzaTopping[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, t] = await Promise.all([
        api.get<Item[]>(`${base}/items/sold-out`),
        api.get<PizzaTopping[]>(`${base}/pizza-toppings`),
      ]);
      setItems(i);
      setToppings(t.filter((x) => x.soldOut));
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'Failed to load the sold-out list.'));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function restore(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await load();
      setError(null);
    } catch (e) {
      setError(errMsg(e, 'Restore failed.'));
    } finally {
      setBusy(false);
    }
  }

  const allClear = items?.length === 0 && toppings?.length === 0;

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Store · Menu · Sold out</div>
          <h1>Sold out today</h1>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Link className="btn btn-outline btn-sm" to={`/r/${rid}/s/${sid}/menu`}>Back to menu</Link>
        </div>
      </div>
      {error && <div className="err">{error}</div>}
      {(items === null || toppings === null) && <div className="loading">Loading…</div>}
      {allClear && <div className="loading">Nothing is sold out — the whole menu is live. 🎉</div>}

      {items !== null && toppings !== null && !allClear && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          <div className="panel">
            <h2>Items</h2>
            {items.length === 0 && <div className="loading">No items sold out.</div>}
            {items.map((it) => (
              <div key={it.itemId} className="rowline">
                <div className="grow">
                  <b>
                    <Link to={`/r/${rid}/s/${sid}/menu/items/${it.itemId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {it.name}
                    </Link>
                  </b>
                  <span className="sub">{TYPE_LABEL[it.itemType] ?? it.itemType}</span>
                </div>
                <span className="chip chip-sold_out">sold out</span>
                <button className="btn btn-primary btn-sm" disabled={busy}
                  onClick={() => void restore(() => api.put(`${base}/items/${it.itemId}/sold-out`, { soldOut: false }))}>
                  <Undo2 size={14} /> Restore
                </button>
              </div>
            ))}
          </div>

          <div className="panel">
            <h2>Pizza toppings</h2>
            {toppings.length === 0 && <div className="loading">No toppings sold out.</div>}
            {toppings.map((t) => (
              <div key={t.toppingId} className="rowline">
                <div className="grow">
                  <b>{t.name}</b>
                  <span className="sub">Topping</span>
                </div>
                <span className="chip chip-sold_out">sold out</span>
                <button className="btn btn-primary btn-sm" disabled={busy}
                  onClick={() => void restore(() => api.put(`${base}/pizza-toppings/${t.toppingId}/sold-out`, { soldOut: false }))}>
                  <Undo2 size={14} /> Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
